import {
  useMutation,
  useQuery as useReactQuery,
  useQueryClient,
} from "@tanstack/react-query";

import FetchClients, { ClientOutput } from "../client";
import { ARCADIA_STATEMENTS_KEY } from "./statements";

export type Rule = ClientOutput["arcadia"]["Rule"];
export type RuleType = ClientOutput["arcadia"]["RuleType"];
export type RuleRequest = ClientOutput["arcadia"]["RuleRequest"];

interface RuleConfig {
  hasValue?: boolean;
  hasConditionValue?: boolean;
}
export const RULES_CONFIG: Record<RuleType, RuleConfig> = {
  APPROVE_METER: {
    hasValue: false,
    hasConditionValue: false,
  },
  IGNORE_METER: {
    hasValue: false,
    hasConditionValue: false,
  },
  SET_METER_COST_ONLY: {
    hasValue: false,
    hasConditionValue: false,
  },
  OVERRIDE_METER_ID: {
    hasValue: true,
    hasConditionValue: false,
  },
  SET_CONSUMPTION_UNIT: {
    hasValue: true,
    hasConditionValue: false,
  },
  UPDATE_CONSUMPTION_UNIT: {
    hasValue: true,
    hasConditionValue: true,
  },
};

const rules = {
  useQueryAll,
  useQuery,
  usePost,
  useDelete,
  usePatch,
} as const;

export default rules;

const ARCADIA_RULE_KEY = "arcadia/accounts";

function useQueryAll(arc_meter_ids: string[]) {
  return useReactQuery({
    queryKey: [ARCADIA_RULE_KEY, "all", arc_meter_ids],
    queryFn: async () => {
      const { data, error } = await FetchClients.arcadia.GET("/rules", {
        params: {
          query: {
            arc_meter_ids,
          },
        },
      });
      if (data) return data;
      throw new Error(error);
    },
    enabled: arc_meter_ids.length > 0,
  });
}

function useQuery(rule_id?: number) {
  return useReactQuery({
    queryKey: [ARCADIA_RULE_KEY, rule_id],
    queryFn: async () => {
      const { data, error } = await FetchClients.arcadia.GET(
        "/rules/{rule_id}",
        {
          params: {
            path: {
              rule_id: rule_id!,
            },
          },
        }
      );
      if (data) return data;
      throw new Error(error);
    },
    enabled: rule_id !== undefined,
  });
}

function usePost() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (req: ClientOutput["arcadia"]["RuleRequest"]) => {
      const { data, error } = await FetchClients.arcadia.POST("/rules", {
        body: req,
      });
      if (data) return data;
      throw new Error(error);
    },
    onError: (e) => {
      throw new Error(
        `Unable to create new rule:
        ${(e as Error)?.message || "Unknown Error"}`
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [ARCADIA_RULE_KEY, "all"],
      });
      queryClient.invalidateQueries({
        queryKey: [ARCADIA_STATEMENTS_KEY],
      });
    },
  });
}

function useDelete() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (rule_id: number) => {
      const { data, error } = await FetchClients.arcadia.DELETE(
        "/rules/{rule_id}",
        {
          params: {
            path: {
              rule_id,
            },
          },
        }
      );
      if (data) return data;
      throw new Error(error);
    },
    onError: (e) => {
      throw new Error(
        `Unable to delete rule:
        ${(e as Error)?.message || "Unknown Error"}`
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [ARCADIA_RULE_KEY, "all"],
      });
      queryClient.invalidateQueries({
        queryKey: [ARCADIA_STATEMENTS_KEY],
      });
    },
  });
}

type PatchRuleParams = ClientOutput["arcadia"]["RuleRequest"] & {
  rule_id: number;
};
function usePatch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ rule_id, ...req }: PatchRuleParams) => {
      const { data, error } = await FetchClients.arcadia.PATCH(
        "/rules/{rule_id}",
        {
          params: {
            path: {
              rule_id,
            },
          },
          body: req,
        }
      );
      if (data) return data;
      throw new Error(error);
    },
    onError: (e) => {
      throw new Error(
        `Unable to update rule:
        ${(e as Error)?.message || "Unknown Error"}`
      );
    },
    onSuccess: ({ id }) => {
      queryClient.invalidateQueries({
        queryKey: [ARCADIA_RULE_KEY, id],
      });
      queryClient.invalidateQueries({
        queryKey: [ARCADIA_RULE_KEY, "all"],
      });
      queryClient.invalidateQueries({
        queryKey: [ARCADIA_STATEMENTS_KEY],
      });
    },
  });
}
